import _ from 'lodash';
import React, { useState } from 'react';
import { Box } from '@dutchie/common/components/rebass-compat';
import styled from 'styled-components';

import useStores from 'shared/hooks/use-stores';
import { getRangeForQuantityDropdown, getQuantityRemaining } from 'shared/helpers/products';
import useCustomerState from 'hooks/use-customer-state';

import { StyledLink } from 'shared/components';
import {
  CheckoutLabel,
  CheckoutLabelGrouping,
  CheckoutBody,
  CheckoutSection,
  CheckoutSectionHeading,
} from 'checkout/components/styles/common';
import { DefaultInput } from 'shared/components/text-input-with-label';
import { observer } from 'mobx-react';
import SummaryItem from 'checkout/components/sections/summary/summary-item';
import { useUpdateServerCheckout } from 'checkout/isolated/hooks';

function getProductIdAndOptionFromKey(itemKey) {
  const [productId, option] = itemKey.split('-');

  return {
    productId,
    option,
  };
}

function Summary() {
  const [enteringSpecialInstructions, setEnteringSpecialInstructions] = useState(false);
  const { Cart, UI, Modals } = useStores();
  const { costBreakdown, order, changeItemQuantity, removeItem, discountToCartApplied } = Cart;
  const { cart, dispensary } = order;
  const disableSpecialInstructions = dispensary?.storeSettings?.disableSpecialInstructions;
  const { isDemo, isEmbedded, isKiosk, isStoreFront, isMobileEcommApp } = UI;
  const fallbackCustomerState = useCustomerState();
  const customerState = Cart.customerState || fallbackCustomerState;
  const { handleUpdateQuantity, handleRemoveItem } = useUpdateServerCheckout();

  function handleSpecialInstructionsChange(event) {
    Cart.setSpecialInstructions(event.target.value);
  }

  const handleRemoveItemServerCheckout = async (itemKey) => {
    const { productId, option } = getProductIdAndOptionFromKey(itemKey);
    await handleRemoveItem({ productId, option });
  };

  const handleUpdateQuantityServerCheckout = async (itemKey, quantity) => {
    const { productId, option } = getProductIdAndOptionFromKey(itemKey);
    await handleUpdateQuantity({ productId, option, quantity });
  };

  return (
    <CheckoutSection>
      <CheckoutLabelGrouping>
        <CheckoutLabel card>
          <CheckoutSectionHeading>Summary</CheckoutSectionHeading>
        </CheckoutLabel>

        <CheckoutBody data-testid='summary-section'>
          {_.map(cart, (item, key) => {
            const { product, option, additionalOption, quantity, price, basePrice } = item;

            const formattedOption =
              ['Flower', 'Concentrate', 'Vaporizers'].includes(product.type) && option.replace('gC', 'g');

            const quantitiesAvailableForSelection = getRangeForQuantityDropdown({
              product,
              option,
              quantityInCart: quantity,
              isKiosk,
            });

            const totalQuantityAvailable = getQuantityRemaining(product, option, { isKiosk });

            const productId = product?.id;
            const receiptItem = _.find(costBreakdown?.receipt?.details?.products, { id: productId, option });

            return (
              <SummaryItem
                additionalOption={additionalOption}
                key={key}
                onQuantityChange={(newQuantity) => {
                  changeItemQuantity(key, Number(newQuantity), UI.showErnie, customerState, () =>
                    handleUpdateQuantityServerCheckout(key, Number(newQuantity))
                  );
                }}
                onRemoveItem={() => {
                  if (_.size(cart) === 1) {
                    // FIXME: we should use the ModalNames type. was not able to import from shared
                    Modals.openModal(`emptyCart`, {
                      clearLastServerCheckoutItem: () => handleRemoveItemServerCheckout(key),
                    });
                  } else {
                    handleRemoveItemServerCheckout(key);
                    removeItem(key);
                  }
                }}
                formattedOption={formattedOption}
                option={option}
                price={price}
                discountToCartApplied={discountToCartApplied}
                bogoDiscount={item.bogoDiscount || Cart.items?.[key]?.bogoDiscount}
                cartItem={Cart.items?.[key] || product}
                product={product}
                dispensary={dispensary}
                quantity={quantity}
                quantitiesAvailableForSelection={quantitiesAvailableForSelection}
                totalQuantityAvailable={totalQuantityAvailable}
                brandName={product.brandName}
                receiptItem={receiptItem}
                saleAdjustment={item.saleAdjustment || Cart.items?.[key]?.saleAdjustment}
                isDemo={isDemo}
                isEmbedded={isEmbedded}
                isKiosk={isKiosk}
                isStoreFront={isStoreFront}
                isMobileEcommApp={isMobileEcommApp}
                isMedical={Cart.isMedical}
                basePrice={basePrice}
                discountedBasePrice={price / quantity}
                isCalculating={Cart.isCalculating}
              />
            );
          })}
          {!disableSpecialInstructions && !enteringSpecialInstructions && (
            <Box mt='30px'>
              <StyledLink as='button' fontSize='13px' onClick={() => setEnteringSpecialInstructions(true)}>
                + Special Instructions
              </StyledLink>
            </Box>
          )}
          {!disableSpecialInstructions && enteringSpecialInstructions && (
            <StyledDefaultInput
              id='specialInstructions'
              label='Special instructions'
              onChange={handleSpecialInstructionsChange}
              value={Cart.order.specialInstructions}
              mt='30px'
              width='100%'
              inputProps={{ maxLength: 500 }}
            />
          )}
        </CheckoutBody>
      </CheckoutLabelGrouping>
    </CheckoutSection>
  );
}

export default observer(Summary);

const StyledDefaultInput = styled(DefaultInput)`
  max-width: 390px;
`;
